import { css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { connect } from 'pwa-helpers';
import store from '../../../store/store';
import { BaseComponent } from '../../base-component/base-component';
import emit from '../../../common/events';
import { AreasOfInterest, RepresentativeType } from '../interfaces/representative.interface';
import { enumConverter } from '../../../common/converters';
import variables from '../../../common/styles/variables';
import { ariaLabelWithNewTab } from '../../../common/utils/a11y-util';

/**
 * Component to display a Representative card.
 */
@customElement('mhecomm-representative-card')
export class RepresentativeCard extends connect(store)(BaseComponent) {

  static styles = [
    variables,
    css`
      .hidden-contact {
        min-height: 3vh;
      }
    `,
  ];

  /**
   * Representative name.
   */
  @property({type: String})
  name: string = '';

  /**
   * Representative areas of interest, formatted as a comma-separated string.
   */
  @property({type: Array})
  areas: AreasOfInterest[] = [];

  /**
   * Representative email address.
   */
  @property({type: String})
  email: string = '';

  /**
   * Representative phone number.
   */
  @property({type: String})
  phone: string = '';

  @property({converter: enumConverter(RepresentativeType)})
  type: RepresentativeType = RepresentativeType.LEARNING_TECHNOLOGY_REPRESENTATIVE;

  @state()
  showContactInfo: boolean = false;

  protected updated(_changedProperties: Map<string | number | symbol, unknown>): void {
    super.updated(_changedProperties);
    if (_changedProperties.has('areas')) {
      this.areas.sort((a, b) => {
        if (a.group && b.group) {
          return a.group.localeCompare(b.group);
        }
        return 0;
      });
    }
  }

  renderComp() {
    return html`
      <div class="col my-2">
        <div class="card rounded bg-white shadow h-100">
          <div class="card-body">
            <div class="row">
              <div class="col-lg-2">
                <mhecomm-person-avatar name="${this.name}"></mhecomm-person-avatar>
              </div>
              <div class="d-lg-none px-2">
                <hr />
              </div>
              <div class="col-lg-6 col-xl-7">
                <div class="fw-bold text-center text-lg-start text-gray mb-2">${this.type}</div>
                <h4 class="fw-semibold text-center text-lg-start mb-2">${this.name}</h4>
                <div class="d-lg-none">
                  <hr />
                </div>
                ${this.type == RepresentativeType.ENTERPRISE_ACCOUNT_MANAGER 
                  ? this.showServicesAndSolutions() 
                  : this.showAreasOfInterest()}
              </div>
              <div class="col-lg-4 col-xl-3 d-flex flex-column align-items-start align-items-lg-center">
                ${!this.showContactInfo ? html`
                  <button class="btn btn-primary w-100 w-lg-75" type="button" @click="${() => this.onShowContactInfoClick()}">
                  <uxt-icon icon="eye" aria-hidden="true"></uxt-icon>&nbsp;Show Contact Info
                  </button>
                  <div class="hidden-contact bg-light mt-2 rounded-3 w-100 w-lg-75 h-50"></div>
                ` : html`
                  <div class="text-start text-lg-end">
                    <div class="fw-bold mb-1">Contact Info</div>
                    <a class="text-break" @click=${() => this.onEmailClick()}
                       href="mailto:${this.email}">${this.email}</a>
                    <div>${this.phone}</div>
                  </div>
                `}
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private showServicesAndSolutions() {
    return html`
      <h5 class="fw-bold fs-6 mb-1">Services and Solutions</h5>
      <ul>
        <li>
          <a 
            class="fw-bold" 
            href="https://www.mheducation.com/highered/campuswide-solutions/sharpen-advantage.html" 
            @click=${() => this.onSharpenAdvantageClick()}
            aria-label="${ariaLabelWithNewTab('Sharpen Advantage')}"
            target="_blank">
            Sharpen Advantage</a>:
          The responsible AI academic success and study app that helps scale academic support and improve student outcomes.
        </li>
        <li>
          <a 
            class="fw-bold" 
            href="https://www.mheducation.com/highered/services/affordable-access.html"
            @click=${() => this.onAffordableAccessClick()}
            aria-label="${ariaLabelWithNewTab('Affordable Access Programs')}"
            target="_blank">
            Affordable Access Programs</a>:
          Course material distribution models designed by institutions and their bookstores that provide students with essential learning resources at significantly reduced costs for the first day of class.
        </li>
      </ul>
    `;
  }

  private showAreasOfInterest() {
    return html`
      <h5 class="fw-bold fs-6 mb-1">Areas of Interest</h5>
      <p class="card-text">${this.getFormattedAreas()}</p>
    `;
  }

  private getFormattedAreas() {
    if (this.areas.length === 1 && !this.areas[0].group) {
      return html`
        <div class="mb-2 mb-lg-0">
          ${this.areas[0].areas.replace(/,/g, ', ')}
        </div>
      `;
    }
    return html`
      <ul>
        ${this.areas.map(area => html`
          <li>
            <span class="fw-bold">${area.group}:</span>
            ${area.areas.replace(/,/g, ', ')}
          </li>
        `)}
      </ul>
    `;
  }

  private onShowContactInfoClick() {
    this.emitAnalyticsEvent('representative-contact-info-click');
    this.showContactInfo = true;
  }

  private onEmailClick() {
    this.emitAnalyticsEvent('representative-email-click');
  }

  private onAffordableAccessClick() {
    this.emitAnalyticsEvent('affordable-access-click');
  }

  private onSharpenAdvantageClick() {
    this.emitAnalyticsEvent('sharpen-advantage-click');
  }

  private emitAnalyticsEvent(eventName: string) {
    emit({
      element: this,
      name: eventName,
      detail: this.getAnalyticsDetails(eventName)
    });
  }

  private getAnalyticsDetails(eventName: string) {
    return {
      eventName: eventName,
      repName: `${this.name}`,
      repType: `${this.type}`,
      ...(this.type != RepresentativeType.ENTERPRISE_ACCOUNT_MANAGER && {
        areasOfInterest: this.getAnalyticsAreas(),
      }),
    };
  }

  private getAnalyticsAreas() {
    return this.areas.map(area => area.areas).join(', ');
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-representative-card': RepresentativeCard;
  }
}
